import { useEffect, useState, useCallback, useRef } from 'react'
import { Save, CheckCircle, CheckCircle2, AlertCircle, Building2, FileText, Copy, MoreHorizontal, Trash2, Sparkles, Loader2, ClipboardList, History, GitBranch, CalendarPlus, ArrowUpCircle, Download, Lock, Unlock, DollarSign, Ban, MessageSquareText, Search, LinkIcon, ExternalLink } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { getDefaultScopeData, useScopeStore } from './store'
import { useHomeStore } from '@/features/home/store'
import { useAuthStore } from '@store/authStore'
import { useSettingsStore } from '@/features/settings/store'
import { dealsApi, scopesApi, scopeVersionsApi, hubspotApi, pipelineProductsApi, quotesApi, presalesDashboardApi, sowTemplateApi, type ResellerChildScope, type SowTemplateType } from '@/api/client'
import type { Quote, DealWithRelations } from '@api/types'
import { showToast } from '@store/uiStore'
import { Modal, ModalFooter } from '@/components/ui/Modal'
import { BlueprintTab } from './components/BlueprintTab'
import { generateBlueprintText, exportBlueprintPdf } from './utils/blueprintExport'
import { GeneralInfoTab } from './components/GeneralInfoTab'
import { KycConfigTab } from './components/KycConfigTab'
import { AmlConfigTab } from './components/AmlConfigTab'
import { BiometricsTab } from './components/BiometricsTab'
import { KybConfigTab } from './components/KybConfigTab'
import { WorkflowsTab } from './components/WorkflowsTab'
import { RulesTab } from './components/RulesTab'
import { HoursTab } from './components/HoursTab'
import { ActionsTab } from './components/ActionsTab'
import { SowTab } from './components/SowTab'
import { CustomWorkTab } from './components/CustomWorkTab'
import { AdvancedTab } from './components/AdvancedTab'
import { ScopeLogModal } from './components/ScopeLogModal'
import { QuotePreviewModal } from './components/QuotePreviewModal'
import { QualificationModal } from './components/QualificationModal'
import { VersionHistoryModal } from './components/VersionHistoryModal'
import { TranscriptsModal } from './components/TranscriptsModal'
import { InjectImplFeeWidget } from './components/InjectImplFeeWidget'
import { deriveConstraints } from './utils/resellerConstraints'
import { mapDbScopeToStoreState, mapStoreStateToDbScope } from './utils/mappers'
import { exportScopeToPdf, exportSowToPdf } from './utils/exporters'
import { getDefaultSowConfig } from './utils/defaults'
import { mergeSowWithTemplate } from './utils/sowSections'
import type { SowConfig } from './types'
import { getDemoScopeData } from './utils/demoScopeData'
import { verifySaveRoundTrip, logVerifyResult } from './utils/saveVerifier'
import type { AutoSaveStatus } from '@hooks/useAutoSave'
import { AutoSaveIndicator } from '@hooks/AutoSaveIndicator'
import { EmptyState } from '@components/ui/EmptyState'

const TABS = [
  { id: 'blueprint', label: 'Blueprint', completionKey: 'blueprint' },
  { id: 'general', label: 'General', completionKey: 'general' },
  { id: 'kyc', label: 'KYC', completionKey: 'kyc' },
  { id: 'aml', label: 'AML', completionKey: 'aml' },
  { id: 'biometrics', label: 'ID Verification', completionKey: 'biometrics' },
  { id: 'kyb', label: 'KYB', completionKey: 'kyb' },
  { id: 'recipes', label: 'Recipes/Workflows', completionKey: 'recipes' },
  { id: 'rules', label: 'Risk & Fraud', completionKey: 'rules' },
  { id: 'hours', label: 'Hours', completionKey: 'hours' },
  { id: 'actions', label: 'Actions', completionKey: 'actions' },
  { id: 'sow', label: 'SOW', completionKey: 'sow' },
  { id: 'custom_work', label: 'Custom Work', completionKey: 'custom_work' },
  { id: 'advanced', label: 'Advanced', completionKey: 'advanced' },
]

export function ScopePage() {
  const [activeTab, setActiveTab] = useState('general')
  const { scope, tabCompletions, updateCustomerName, updateCustomerAbn, updateGeneralInfo, finalizeScope, unfinalizeScope, setScope } =
    useScopeStore()
  const { selectedDeal, searchQuery, setSearchQuery, searchDeals, searchResults, isSearching, hasSearched, clearSearch } = useHomeStore()
  const { user } = useAuthStore()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [actionsOpen, setActionsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle')
  const lastSavedRef = useRef<string>('')
  const saveInFlightRef = useRef(false)
  const lastLoadedDealIdRef = useRef<string | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const performSaveRef = useRef<() => Promise<void>>(() => Promise.resolve())
  const apiLoadCompleteRef = useRef(false)
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false)
  const [duplicateName, setDuplicateName] = useState('')
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [showScopeLog, setShowScopeLog] = useState(false)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [showTranscripts, setShowTranscripts] = useState(false)
  const [showNextCallFocus, setShowNextCallFocus] = useState(false)
  const [nextCallItems, setNextCallItems] = useState<string[] | null>(null)
  const [nextCallLoading, setNextCallLoading] = useState(false)
  const [nextCallError, setNextCallError] = useState<string | null>(null)
  const [savingVersion, setSavingVersion] = useState(false)
  const [addingToPP, setAddingToPP] = useState(false)
  const [resellerChildren, setResellerChildren] = useState<ResellerChildScope[]>([])

  const { resellerConstraints, parentCustomerName, setResellerConstraints } = useScopeStore()
  const [isClearing, setIsClearing] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [markedLost, setMarkedLost] = useState(false)
  const [markingLost, setMarkingLost] = useState(false)
  const [presalesDealId, setPresalesDealId] = useState<number | null>(null)
  const [cloneHubSpotDeal, setCloneHubSpotDeal] = useState<null | {
    id: string
    name: string
  }>(null)
  const [isExtractingContract, setIsExtractingContract] = useState(false)
  const [showCloneFrom, setShowCloneFrom] = useState(false)
  const [cloneFromSearch, setCloneFromSearch] = useState('')
  const [cloneFromResults, setCloneFromResults] = useState<Array<{ id: number; customer_name: string; hubspot_deal_name: string | null }>>([])
  const [cloneFromSearching, setCloneFromSearching] = useState(false)
  const [cloneFromDeal, setCloneFromDeal] = useState<{ id: number; name: string } | null>(null)
  const [isCloning, setIsCloning] = useState(false)
  const [linkedQuote, setLinkedQuote] = useState<Quote | null>(null)
  // Deal record with impl-review state + nested quote/scope, for the InjectImplFeeWidget.
  const [dealMeta, setDealMeta] = useState<DealWithRelations | null>(null)
  const [showQuotePreview, setShowQuotePreview] = useState(false)
  const [qualification, setQualification] = useState<Record<string, string> | null>(null)
  const [showQualification, setShowQualification] = useState(false)
  const [latestMsa, setLatestMsa] = useState<{ name: string; signedUrl?: string } | null>(null)
  const [showLinkHubspot, setShowLinkHubspot] = useState(false)
  const [linkSearch, setLinkSearch] = useState('')
  const [linkSearchResults, setLinkSearchResults] = useState<Array<{ id: string; name: string; company: string | null; stage: string }>>([])
  const [linkSearching, setLinkSearching] = useState(false)
  const [linkSelectedDeal, setLinkSelectedDeal] = useState<{ id: string; name: string } | null>(null)
  const [showLinkConfirm, setShowLinkConfirm] = useState(false)
  const [isLinking, setIsLinking] = useState(false)

  const isLocalDev =
    Boolean(import.meta.env.DEV) ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '::1'

  // Set initial tab from URL query param (e.g. ?tab=blueprint)
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab) setActiveTab(tab)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const scopeStatus = scope.generalInfo.scopeStatus

  const STATUS_STEPS: Array<{ id: typeof scopeStatus; label: string }> = [
    { id: 'draft', label: 'Draft' },
    { id: 'implementations', label: 'Impl' },
    { id: 'sent', label: 'Sent' },
    { id: 'adjust', label: 'Adjust' },
    { id: 'confirm', label: 'Confirm' },
    { id: 'handover', label: 'Handover' },
  ]

  const formatStageDate = (iso: string | null | undefined) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString()
  }

  const handleStartDemoSession = useCallback(async () => {
    if (!isLocalDev) return
    setIsLoading(true)
    try {
      const response = await dealsApi.create({
        customer_name: 'Local Demo Deal',
        created_by: user?.email || undefined,
        created_by_name: user?.name || undefined,
      })

      if (!response.success || !response.data) {
        showToast(`Failed to start demo session: ${response.error || 'Unknown error'}`, 'error')
        return
      }

      showToast('Demo session created', 'success')
      navigate(`/scope?session=${response.data.id}`)
    } catch (e) {
      console.error('Start demo session failed:', e)
      const fallbackSessionId = `local-demo-${Date.now()}`
      showToast('Backend demo session failed. Starting local demo mode instead.', 'warning')
      setScope(getDemoScopeData())
      navigate(`/scope?session=${fallbackSessionId}`)
    } finally {
      setIsLoading(false)
    }
  }, [isLocalDev, navigate, user])

  const handleDuplicateDeal = async () => {
    if (!numericDealId) {
      showToast('Please link a deal before duplicating. Go to Home and select a deal first.', 'error')
      return
    }

    setIsDuplicating(true)
    try {
      if (!cloneHubSpotDeal?.id || !cloneHubSpotDeal?.name) {
        showToast('Please select a HubSpot deal to link the duplicated deal to.', 'error')
        return
      }

      const response = await dealsApi.clone(numericDealId, {
        customer_name: duplicateName.trim() || undefined,
        created_by: user?.email || undefined,
        created_by_name: user?.name || undefined,
        hubspot_deal_id: cloneHubSpotDeal.id,
        hubspot_deal_name: cloneHubSpotDeal.name,
      })

      if (!response.success || !response.data) {
        showToast(`Failed to duplicate deal: ${response.error || 'Unknown error'}`, 'error')
        return
      }

      showToast('Deal duplicated', 'success')
      setShowDuplicateConfirm(false)
      setDuplicateName('')
      setCloneHubSpotDeal(null)
      clearSearch()
      navigate(`/scope?session=${response.data.id}`)
    } catch (e) {
      console.error('Deal clone failed:', e)
      showToast('Failed to duplicate deal. Please try again.', 'error')
    } finally {
      setIsDuplicating(false)
    }
  }

  const handleCloneFromSearch = async (query: string) => {
    setCloneFromSearch(query)
    if (query.length < 2) { setCloneFromResults([]); return }
    setCloneFromSearching(true)
    try {
      const res = await dealsApi.list({ search: query, pageSize: 10 })
      if (res.success && res.data) {
        setCloneFromResults(
          res.data.items
            .filter(d => d.id !== numericDealId)
            .map(d => ({
              id: d.id,
              customer_name: d.customer_name || '',
              hubspot_deal_name: (d as unknown as Record<string, unknown>).hubspot_deal_name as string | null,
            }))
        )
      }
    } catch { /* ignore */ }
    setCloneFromSearching(false)
  }

  const handleCloneFromDeal = async () => {
    if (!cloneFromDeal || !numericDealId) return
    setIsCloning(true)
    try {
      const response = await scopesApi.getByDealId(cloneFromDeal.id)
      if (!response.success || !response.data) {
        showToast('Source deal has no scope data to clone', 'error')
        return
      }
      const mapped = mapDbScopeToStoreState(response.data)
      // Keep the current deal's customer name and scope status
      mapped.customerName = scope.customerName
      mapped.customerAbn = scope.customerAbn
      mapped.generalInfo.scopeStatus = scope.generalInfo.scopeStatus
      mapped.generalInfo.scopeStatusDates = scope.generalInfo.scopeStatusDates
      mapped.finalized = false
      setScope(mapped)
      lastSavedRef.current = '' // Force auto-save to pick it up
      showToast(`Scope cloned from "${cloneFromDeal.name}"`, 'success')
      setShowCloneFrom(false)
      setCloneFromDeal(null)
      setCloneFromSearch('')
      setCloneFromResults([])
    } catch (e) {
      console.error('Clone from deal failed:', e)
      showToast('Failed to clone scope. Please try again.', 'error')
    } finally {
      setIsCloning(false)
    }
  }

  const handleLinkHubspotSearch = async (query: string) => {
    setLinkSearch(query)
    if (query.length < 2) { setLinkSearchResults([]); return }
    setLinkSearching(true)
    try {
      const res = await hubspotApi.searchDeals(query)
      if (res.success && res.data?.results) {
        setLinkSearchResults(res.data.results.map((d: any) => ({
          id: d.id,
          name: d.properties?.dealname || 'Unnamed Deal',
          company: d.properties?.company || null,
          stage: d.properties?.dealstage || 'Unknown',
        })))
      }
    } catch { /* ignore */ }
    setLinkSearching(false)
  }

  const handleConfirmLinkHubspot = async () => {
    if (!linkSelectedDeal || !numericDealId) return
    setIsLinking(true)
    try {
      const res = await dealsApi.update(numericDealId, {
        hubspot_deal_id: linkSelectedDeal.id,
        hubspot_deal_name: linkSelectedDeal.name,
      } as any)
      if (!res.success) {
        showToast(res.error || 'Failed to link deal', 'error')
        return
      }
      // Update the selectedDeal in the home store so the UI reflects the change
      useHomeStore.setState((state) => ({
        selectedDeal: state.selectedDeal ? {
          ...state.selectedDeal,
          hubspotDealId: linkSelectedDeal.id,
          name: linkSelectedDeal.name,
        } : state.selectedDeal,
      }))
      showToast(`Linked to HubSpot deal: ${linkSelectedDeal.name}`, 'success')
      setShowLinkConfirm(false)
      setShowLinkHubspot(false)
      setLinkSelectedDeal(null)
      setLinkSearch('')
      setLinkSearchResults([])
    } catch {
      showToast('Failed to link deal', 'error')
    } finally {
      setIsLinking(false)
    }
  }

  const setScopeStage = async (next: typeof scopeStatus) => {
    const now = new Date().toISOString()
    const dates = scope.generalInfo.scopeStatusDates

    const nextDates = {
      ...dates,
      [next]: next === 'draft' && dates?.draft ? dates.draft : now,
    }

    // Sync scope_confirmed action with scope status
    const isConfirmed = next === 'confirm' || next === 'handover' || next === 'implementations'
    const actions = [...(scope.generalInfo.presalesActions || [])]
    const existing = actions.find(a => a.id === 'scope_confirmed')
    if (existing) {
      existing.completed = isConfirmed
      existing.completedAt = isConfirmed ? now.split('T')[0] : null
    } else if (isConfirmed) {
      actions.push({ id: 'scope_confirmed', completed: true, completedAt: now.split('T')[0], notRequired: false, notes: '' })
    }

    updateGeneralInfo('scopeStatus', next)
    updateGeneralInfo('scopeStatusDates', nextDates as any)
    updateGeneralInfo('presalesActions', actions)

    // Auto-lock on sent or handover (not implementations — impl team may still edit)
    if ((next === 'sent' || next === 'handover') && !scope.finalized) {
      finalizeScope(user?.name || user?.email || 'Unknown')
    }
    // Auto-unlock when moving away from sent/handover
    if (next !== 'sent' && next !== 'handover' && scope.finalized) {
      unfinalizeScope()
    }

    // Button clicks don't trigger focusout, so auto-save never fires.
    // Build the updated scope and persist immediately.
    if (numericDealId) {
      const autoLock = (next === 'sent' || next === 'handover') && !scope.finalized
      const autoUnlock = next !== 'sent' && next !== 'handover' && scope.finalized
      const updatedScope = {
        ...scope,
        generalInfo: { ...scope.generalInfo, scopeStatus: next, scopeStatusDates: nextDates, presalesActions: actions },
        ...(autoLock ? { finalized: true, finalizedAt: now, finalizedBy: user?.name || user?.email || 'Unknown' } : {}),
        ...(autoUnlock ? { finalized: false, finalizedAt: null, finalizedBy: null } : {}),
      }
      try {
        setAutoSaveStatus('saving')
        await scopesApi.save(numericDealId, {
          ...mapStoreStateToDbScope(updatedScope, useSettingsStore.getState().hourSettings),
          _user_email: user?.email,
          _user_name: user?.name,
        } as any)
        lastSavedRef.current = JSON.stringify(updatedScope)
        setAutoSaveStatus('saved')
        showToast('Saved', 'success', 1200)
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
        savedTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 1500)
      } catch (e) {
        console.error('Failed to save status change:', e)
        setAutoSaveStatus('error')
        showToast('Failed to save status', 'error')
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
        savedTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 2500)
      }
    }
  }

  const sessionDealId = searchParams.get('session')
  // Only fall back to selectedDeal.id if it's an internal DB ID (has hubspotDealId set separately).
  // When selectedDeal comes from raw HubSpot search, id IS the HubSpot ID and no DB record exists yet.
  const selectedDealInternalId = selectedDeal?.hubspotDealId ? selectedDeal.id : null
  const dealId = sessionDealId || selectedDealInternalId || null
  const isNumericDealId = (id: string | null): id is string => Boolean(id && /^[0-9]+$/.test(id))
  const numericDealId = isNumericDealId(dealId) ? Number(dealId) : null
  const isLocalDemoSession = Boolean(isLocalDev && sessionDealId && sessionDealId.startsWith('local-demo-'))
  const hasLinkedDeal = Boolean(numericDealId) || isLocalDemoSession

  useEffect(() => {
    if (sessionDealId) return
    if (!selectedDealInternalId) return
    navigate(`/scope?session=${selectedDealInternalId}`, { replace: true })
  }, [navigate, selectedDealInternalId, sessionDealId])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActionsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Read scope directly from Zustand store (not from render closure) to
  // guarantee we always serialise the very latest state.  This eliminates a
  // stale-closure bug where focusout fires before React flushes the latest
  // `scope` into useCallback deps.
  const handleAutoSave = useCallback(async () => {
    if (!numericDealId) return
    const currentScope = useScopeStore.getState().scope
    const response = await scopesApi.save(numericDealId, {
      ...mapStoreStateToDbScope(currentScope, useSettingsStore.getState().hourSettings),
      _user_email: user?.email,
      _user_name: user?.name,
    } as any)
    if (!response.success) {
      throw new Error(response.error || 'Failed to save scope')
    }
  }, [numericDealId, user])

  const handleNextCallFocus = async () => {
    if (!numericDealId) return
    setShowNextCallFocus(true)
    setNextCallLoading(true)
    setNextCallError(null)
    setNextCallItems(null)
    try {
      const res = await scopeVersionsApi.blueprintNextSteps(numericDealId, qualification)
      if (res.success && res.data?.items?.length) {
        setNextCallItems(res.data.items.map((item) => item.text))
      } else {
        setNextCallError(res.data?.error || res.error || 'No suggestions returned')
      }
    } catch {
      setNextCallError('Network error')
    }
    setNextCallLoading(false)
  }

  const handleExportPdf = async () => {
    try {
      await exportScopeToPdf(scope)
    } catch (e) {
      console.error('PDF export failed:', e)
      showToast('PDF export failed. Please try again.', 'error')
    }
  }

  const handleExportSow = async () => {
    try {
      await exportSowToPdf(scope)
      showToast('SOW exported', 'success')
    } catch (e) {
      console.error('SOW export failed:', e)
      showToast('SOW export failed. Please try again.', 'error')
    }
  }

  const handleExtractContractDetails = async () => {
    const hubspotDealId = selectedDeal?.hubspotDealId
    if (!hubspotDealId) {
      showToast('No HubSpot deal linked. Link a deal first.', 'error')
      return
    }
    setIsExtractingContract(true)
    try {
      const res = await hubspotApi.extractContractDetails(hubspotDealId)
      if (!res.success || !res.data) {
        showToast(res.error || 'No contract found on this deal.', 'error')
        return
      }
      const { companyName, abn, fileName } = res.data
      if (companyName) updateCustomerName(companyName)
      if (abn) updateCustomerAbn(abn)
      showToast(
        `Extracted from ${fileName || 'contract'}: ${companyName || '(no name)'}${abn ? ` / ABN: ${abn}` : ''}`,
        'success'
      )
    } catch (e) {
      console.error('Contract extraction failed:', e)
      showToast('Failed to extract contract details.', 'error')
    } finally {
      setIsExtractingContract(false)
    }
  }

  // Expose on window for console access: window.__verifyScopeSave()
  useEffect(() => {
    ;(window as any).__verifyScopeSave = () => {
      const result = verifySaveRoundTrip(scope)
      logVerifyResult(result)
      return result
    }
    return () => { delete (window as any).__verifyScopeSave }
  }, [scope])

  const handleClearAllDetails = async () => {
    if (!numericDealId) return

    setIsClearing(true)
    try {
      const fresh = getDefaultScopeData()
      setScope(fresh)

      const response = await scopesApi.save(numericDealId, {
        ...mapStoreStateToDbScope(fresh, useSettingsStore.getState().hourSettings),
        _user_email: user?.email,
        _user_name: user?.name,
      } as any)

      if (!response.success) {
        throw new Error(response.error || 'Failed to clear scope')
      }

      lastSavedRef.current = JSON.stringify(fresh)
      showToast('Cleared scope details', 'success')
    } catch (e) {
      console.error('Clear details failed', e)
      showToast('Failed to clear scope details', 'error')
    } finally {
      setIsClearing(false)
    }
  }

  const performSaveIfDirty = useCallback(async () => {
    if (!numericDealId || isLoading) return
    if (!apiLoadCompleteRef.current) return
    if (saveInFlightRef.current) return

    // Always read the latest scope from the store, not the render closure.
    const currentScope = useScopeStore.getState().scope
    const serialized = JSON.stringify(currentScope)
    if (!serialized || serialized === lastSavedRef.current) return

    saveInFlightRef.current = true
    setAutoSaveStatus('saving')
    try {
      await handleAutoSave()
      lastSavedRef.current = serialized
      setAutoSaveStatus('saved')
      showToast('Saved', 'success', 1200)

      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 1500)
    } catch (e) {
      console.error('Auto-save failed', e)
      setAutoSaveStatus('error')
      showToast('Failed to save', 'error')
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 2500)
    } finally {
      saveInFlightRef.current = false
    }
  }, [numericDealId, handleAutoSave, isLoading])

  // Keep a ref so cleanup / beforeunload always call the latest version
  useEffect(() => {
    performSaveRef.current = performSaveIfDirty
  }, [performSaveIfDirty])

  // Warn on full page unload if there are unsaved changes, and flush on
  // component unmount (client-side React Router navigation).
  useEffect(() => {
    const warnOnUnload = (e: BeforeUnloadEvent) => {
      const currentScope = useScopeStore.getState().scope
      const serialized = JSON.stringify(currentScope)
      if (serialized && serialized !== lastSavedRef.current) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', warnOnUnload)
    return () => {
      window.removeEventListener('beforeunload', warnOnUnload)
      // Unmount (client-side navigation): flush via the normal async path.
      // performSaveRef always has the latest performSaveIfDirty which reads
      // scope directly from the Zustand store.
      void performSaveRef.current()
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadScope() {
      if (!numericDealId) return

      if (lastLoadedDealIdRef.current !== String(numericDealId)) {
        apiLoadCompleteRef.current = false
        const fresh = getDefaultScopeData()
        setScope(fresh)
        lastSavedRef.current = JSON.stringify(useScopeStore.getState().scope)
        lastLoadedDealIdRef.current = String(numericDealId)
      }

      setIsLoading(true)

      try {
        const response = await scopesApi.getByDealId(numericDealId)
        if (cancelled) return

        if (response.success && response.data) {
          const mapped = mapDbScopeToStoreState(response.data)
          setScope(mapped)
          // Use the post-normalization scope for dirty tracking so the
          // first comparison doesn't see normalization changes as user edits.
          lastSavedRef.current = JSON.stringify(useScopeStore.getState().scope)
          apiLoadCompleteRef.current = true

          // Eagerly merge the correct SOW template so the upsell (or reseller)
          // legal text is present even before the user opens the SOW tab.
          const gi = mapped.generalInfo
          const sowType: SowTemplateType = gi.isUpsell
            ? 'upsell'
            : gi.resellerAgreement === 'yes'
              ? gi.resellerRelationshipType === 'selling_to_customers'
                ? 'reseller_parent'
                : gi.resellerRelationshipType === 'customer_of_reseller'
                  ? 'reseller_child'
                  : 'global'
              : 'global'
          sowTemplateApi.get(sowType).then((res) => {
            if (cancelled) return
            const defaults = getDefaultSowConfig(sowType)
            const templateConfig: SowConfig = { ...defaults, ...(res.data?.config as Partial<SowConfig> ?? {}) }
            const current = useScopeStore.getState().scope
            const merged = mergeSowWithTemplate(current.sowConfig, templateConfig, current.sowCustomizedSections)
            useScopeStore.setState((state) => ({
              scope: { ...state.scope, sowConfig: merged },
            }))
            // Update dirty-tracking baseline so the template merge isn't seen as a user edit
            lastSavedRef.current = JSON.stringify(useScopeStore.getState().scope)
          }).catch(() => { /* template is optional */ })

          // Load parent scope for reseller constraints
          const rawData = response.data as unknown as Record<string, unknown>
          if (rawData.parent_scope_id) {
            try {
              const parentRes = await scopeVersionsApi.getParentScope(numericDealId)
              if (!cancelled && parentRes.success && parentRes.data) {
                const parentData = parentRes.data as Record<string, unknown>
                const constraints = deriveConstraints(parentData)
                const pName = (parentData.customer_name as string) || 'Reseller'
                setResellerConstraints(constraints, pName)
              }
            } catch {
              // Parent scope loading is best-effort
            }
          } else {
            setResellerConstraints(null, null)
          }
          return
        }

        // No scope yet for this deal: ensure we do NOT keep data from a previous session.
        const fresh = getDefaultScopeData()
        setScope(fresh)
        setResellerConstraints(null, null)
        lastSavedRef.current = JSON.stringify(useScopeStore.getState().scope)
        // Only allow auto-save if the API confirmed no scope exists (404).
        // If the API failed (network error, 500, etc.), keep the guard up
        // so auto-save doesn't overwrite real data with defaults.
        if (response.error === 'Scope not found') {
          apiLoadCompleteRef.current = true
        }
      } finally {
        setIsLoading(false)
      }
    }

    if (isLocalDemoSession) {
      setScope(getDemoScopeData())
      lastSavedRef.current = JSON.stringify(getDemoScopeData())
    } else {
      loadScope()
    }

    return () => {
      cancelled = true
    }
  }, [numericDealId, setScope, isLocalDemoSession])

  // Fetch linked quote for this deal
  useEffect(() => {
    if (!numericDealId) { setLinkedQuote(null); return }
    let cancelled = false
    quotesApi.getByDealId(numericDealId).then((res) => {
      if (!cancelled) setLinkedQuote(res.success && res.data ? res.data : null)
    }).catch(() => { if (!cancelled) setLinkedQuote(null) })
    return () => { cancelled = true }
  }, [numericDealId])

  // Load the deal record (impl-review state + nested quote/scope) that drives the impl-team
  // inject widget. Re-fetched after each autosave so the scope's calculated fee stays current
  // — the impl user fills in hours here, then pushes the freshly-calculated fee to the quote.
  const loadDealMeta = useCallback(async () => {
    if (!numericDealId) { setDealMeta(null); return }
    const res = await dealsApi.get(numericDealId)
    setDealMeta(res.success && res.data ? res.data : null)
  }, [numericDealId])

  useEffect(() => { void loadDealMeta() }, [loadDealMeta])

  useEffect(() => {
    if (autoSaveStatus === 'saved') void loadDealMeta()
  }, [autoSaveStatus, loadDealMeta])

  // Fetch HubSpot qualification data and attachments
  // Fall back to scope's hubspotDealId when selectedDeal is restored from DB without it
  const hubspotDealId = selectedDeal?.hubspotDealId || scope.hubspotDealId
  useEffect(() => {
    if (!hubspotDealId) { setQualification(null); setLatestMsa(null); return }
    let cancelled = false
    hubspotApi.getQualification(hubspotDealId).then((res) => {
      if (!cancelled) setQualification(res.success && res.data ? res.data.qualification : null)
    }).catch(() => { if (!cancelled) setQualification(null) })
    hubspotApi.getAttachments(hubspotDealId).then((res) => {
      if (cancelled) return
      if (res.success && res.data && res.data.attachments.length > 0) {
        // Find the first attachment with a signed URL (prefer MSA)
        const withUrl = res.data.attachments.find(a => a.signedUrl)
        if (withUrl) {
          setLatestMsa({ name: withUrl.name, signedUrl: withUrl.signedUrl })
        } else {
          // Still show the name even without URL so user knows it exists
          setLatestMsa({ name: res.data.attachments[0].name })
        }
      } else {
        setLatestMsa(null)
      }
    }).catch(() => { if (!cancelled) setLatestMsa(null) })
    return () => { cancelled = true }
  }, [hubspotDealId])

  useEffect(() => {
    let cancelled = false

    async function restoreSelectedDealFromSession() {
      if (!numericDealId) return
      if (selectedDeal && selectedDeal.id === String(numericDealId)) return

      const response = await dealsApi.get(numericDealId)
      if (cancelled) return
      if (!response.success || !response.data) return

      const deal = response.data as any
      setMarkedLost(!!(deal.dealhub_marked_lost))
      setPresalesDealId(deal.presales_deal_id ?? null)
      useHomeStore.setState({
        selectedDeal: {
          id: deal.id.toString(),
          hubspotDealId: deal.hubspot_deal_id ?? null,
          name: deal.hubspot_deal_name || deal.customer_name || `Deal #${deal.id}`,
          amount: null,
          stage: deal.deal_stage || 'Unknown',
          closeDate: null,
          ownerName: 'Unknown',
          companyName: deal.customer_name ?? null,
          createdAt: deal.created_at,
          closedWon: !!(deal.closed_won),
        },
      })
    }

    void restoreSelectedDealFromSession()

    return () => {
      cancelled = true
    }
  }, [numericDealId, selectedDeal])

  // Load marked-lost status for the current deal
  useEffect(() => {
    if (!numericDealId) return
    let cancelled = false
    dealsApi.get(numericDealId).then(res => {
      if (cancelled || !res.success || !res.data) return
      const d = res.data as any
      setMarkedLost(!!(d.dealhub_marked_lost))
      setPresalesDealId(d.presales_deal_id ?? null)
    })
    return () => { cancelled = true }
  }, [numericDealId])

  // Load reseller children when this scope is a reseller parent
  useEffect(() => {
    if (!numericDealId) return
    const gi = useScopeStore.getState().scope.generalInfo
    if (gi.resellerAgreement !== 'yes') {
      setResellerChildren([])
      return
    }
    let cancelled = false
    scopeVersionsApi.getChildScopes(numericDealId).then((res) => {
      if (!cancelled && res.success && res.data) {
        setResellerChildren(res.data.children)
      }
    })
    return () => { cancelled = true }
  }, [numericDealId, scope.generalInfo.resellerAgreement])

  useEffect(() => {
    const isEditableElement = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement => {
      return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      )
    }

    const onFocusOut = (e: FocusEvent) => {
      if (!isEditableElement(e.target)) return
      void performSaveIfDirty()
    }

    document.addEventListener('focusout', onFocusOut, true)
    return () => {
      document.removeEventListener('focusout', onFocusOut, true)
    }
  }, [performSaveIfDirty])

  // Debounced auto-save: catches state changes from non-text interactions
  // (radio buttons, checkboxes, selects, button clicks) that don't reliably
  // trigger focusout with up-to-date scope. Runs after React re-renders so
  // performSaveIfDirty always has the latest state.
  useEffect(() => {
    if (!numericDealId || isLoading) return
    const timer = setTimeout(() => {
      void performSaveIfDirty()
    }, 1500)
    return () => clearTimeout(timer)
  }, [scope, numericDealId, isLoading, performSaveIfDirty])

  useEffect(() => {
    const isEditableElement = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement => {
      return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      )
    }

    const blockIfFinalized = (e: Event) => {
      if (!scope.finalized) return
      if (!isEditableElement(e.target)) return

      const el = e.target
      if ((el as any).disabled) return

      e.preventDefault()
      e.stopPropagation()
      try {
        el.blur()
      } catch {
        // ignore
      }
      showToast('Finalized, please Unfinalize to unlock.', 'warning', 2000)
    }

    document.addEventListener('focusin', blockIfFinalized, true)
    document.addEventListener('mousedown', blockIfFinalized, true)
    document.addEventListener('change', blockIfFinalized, true)

    return () => {
      document.removeEventListener('focusin', blockIfFinalized, true)
      document.removeEventListener('mousedown', blockIfFinalized, true)
      document.removeEventListener('change', blockIfFinalized, true)
    }
  }, [scope.finalized])

  useEffect(() => {
    let lastScrollTop: number | null = null

    const isSelect = (target: EventTarget | null): target is HTMLSelectElement => {
      return target instanceof HTMLSelectElement
    }

    const isCheckboxOrRadio = (target: EventTarget | null): target is HTMLInputElement => {
      return (
        target instanceof HTMLInputElement &&
        (target.type === 'checkbox' || target.type === 'radio')
      )
    }

    const isScrollSensitiveControl = (target: EventTarget | null): target is HTMLSelectElement | HTMLInputElement => {
      return isSelect(target) || isCheckboxOrRadio(target)
    }

    const getScrollElement = (): HTMLElement => {
      return (document.scrollingElement as HTMLElement) ?? document.documentElement
    }

    const onSelectInteract = (e: Event) => {
      if (isScrollSensitiveControl(e.target)) {
        lastScrollTop = getScrollElement().scrollTop
      }
    }

    const onSelectChange = (e: Event) => {
      if (!isScrollSensitiveControl(e.target)) return
      if (lastScrollTop === null) return

      const el = e.target
      const scrollTop = lastScrollTop
      lastScrollTop = null

      if (document.activeElement === el) {
        el.blur()
      }

      requestAnimationFrame(() => {
        const scrollEl = getScrollElement()
        scrollEl.scrollTop = scrollTop

        setTimeout(() => {
          getScrollElement().scrollTop = scrollTop
        }, 0)
      })
    }

    document.addEventListener('mousedown', onSelectInteract, true)
    document.addEventListener('focusin', onSelectInteract, true)
    document.addEventListener('change', onSelectChange, true)

    return () => {
      document.removeEventListener('mousedown', onSelectInteract, true)
      document.removeEventListener('focusin', onSelectInteract, true)
      document.removeEventListener('change', onSelectChange, true)
    }
  }, [])

  const getProgressLabel = (key: string) => {
    switch (key) {
      case 'kyc':
        return 'KYC'
      case 'aml':
        return 'AML'
      case 'biometrics':
        return 'ID Verification'
      case 'kyb':
        return 'KYB'
      case 'recipes':
        return 'Recipes/Workflows'
      case 'rules':
        return 'Risk & Fraud'
      case 'sow':
        return 'SOW'
      default:
        return key.charAt(0).toUpperCase() + key.slice(1)
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'blueprint':
        return <BlueprintTab dealId={numericDealId} />
      case 'general':
        return <GeneralInfoTab dealId={numericDealId} />
      case 'kyc':
        return <KycConfigTab onSwitchTab={setActiveTab} />
      case 'aml':
        return <AmlConfigTab />
      case 'biometrics':
        return <BiometricsTab />
      case 'kyb':
        return <KybConfigTab />
      case 'recipes':
        return <WorkflowsTab />
      case 'rules':
        return <RulesTab />
      case 'hours':
        return <HoursTab />
      case 'actions':
        return <ActionsTab />
      case 'sow':
        return <SowTab />
      case 'custom_work':
        return <CustomWorkTab />
      case 'advanced':
        return <AdvancedTab />
      default:
        return <GeneralInfoTab dealId={numericDealId} />
    }
  }

  const getCompletionColor = (completion: number) => {
    if (completion === 100) return 'text-success'
    if (completion > 50) return 'text-warning'
    return 'text-[var(--text-muted)]'
  }

  // Hours and SOW tabs don't factor into overall scope completion
  const EXCLUDED_FROM_OVERALL = new Set(['blueprint', 'hours', 'actions', 'sow', 'custom_work', 'advanced'])
  const scopeEntries = Object.entries(tabCompletions).filter(([key]) => !EXCLUDED_FROM_OVERALL.has(key))
  const fieldBasedCompletion = scopeEntries.length > 0
    ? Math.round(scopeEntries.reduce((sum, [, val]) => sum + val, 0) / scopeEntries.length)
    : 0
  const isUpsell = scope.generalInfo.isUpsell || false
  const overallCompletion = isUpsell && scope.generalInfo.scopeConfirmed ? 100 : fieldBasedCompletion

  const isContractSigned = selectedDeal?.closedWon === true || selectedDeal?.stage === 'closedwon'

  return (
    <div className="relative">
      <div className="space-y-6">
      {/* Contract-signed status banner — visible once a deal is linked so SCs and Implementations can see, at a glance, whether work can start. */}
      {hasLinkedDeal && (
        isContractSigned ? (
          <div className="flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/30 rounded-lg text-sm text-success">
            <CheckCircle size={16} />
            <span className="font-medium">Customer contract signed — deal won in HubSpot</span>
          </div>
        ) : (
          <div className="flex items-start gap-3 px-4 py-3 bg-red-500/10 border border-red-500/40 rounded-lg">
            <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-red-400">Customer contract not yet signed</div>
              <div className="text-xs text-red-400/80 mt-0.5">
                Do not begin implementation work. The deal must be moved to Won in HubSpot once the customer signs the commercial contract.
              </div>
            </div>
          </div>
        )
      )}

      {/* DealHub marked-lost banner */}
      {markedLost && hasLinkedDeal && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/40 rounded-lg">
          <Ban size={18} className="text-amber-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-400">Deal marked as lost in DealHub</div>
            <div className="text-xs text-amber-400/80 mt-0.5">
              This deal has been manually marked as lost for reporting purposes (HubSpot may not reflect this yet).
            </div>
          </div>
          <button
            type="button"
            className="shrink-0 px-3 py-1.5 text-xs rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
            onClick={async () => {
              setMarkingLost(true)
              const hubId = selectedDeal?.hubspotDealId || scope.hubspotDealId
              const res = await presalesDashboardApi.markDealLost(presalesDealId || 0, false, hubId || undefined)
              if (res.success) {
                setMarkedLost(false)
                showToast('Deal unmarked as lost', 'success')
              } else {
                showToast('Failed to unmark deal', 'error')
              }
              setMarkingLost(false)
            }}
            disabled={markingLost}
          >
            {markingLost ? 'Updating...' : 'Undo'}
          </button>
        </div>
      )}

      {/* Header - matches Quote format */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-[var(--text-primary)]">Scope / Pre-Sales</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              Configure implementation scope<br />and requirements
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Auto-save indicator */}
            {dealId && <AutoSaveIndicator status={autoSaveStatus} />}

            {resellerConstraints && (
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-xs text-blue-400">
                <GitBranch size={14} />
                Reseller child of {parentCustomerName}
              </div>
            )}

            {scope.finalized && (
              <div className="flex items-center gap-2 px-3 py-1 bg-warning/10 border border-warning/30 rounded-full text-xs text-warning">
                <AlertCircle size={14} />
                LOCKED
              </div>
            )}

            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50"
                onClick={() => setActionsOpen(!actionsOpen)}
                disabled={isLoading}
                title="Options"
              >
                <MoreHorizontal size={16} />
              </button>

              {actionsOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-dropdown z-50">
                  <div className="py-1">
                    <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Actions</div>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        setShowQuotePreview(true)
                      }}
                      disabled={!linkedQuote}
                    >
                      <DollarSign size={14} className={linkedQuote ? 'text-green-400' : 'text-[var(--text-muted)]'} />
                      {linkedQuote ? 'View linked quote' : 'View linked quote (none)'}
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        setShowQualification(true)
                      }}
                      disabled={!qualification || Object.keys(qualification).length === 0}
                    >
                      <ClipboardList size={14} className={qualification && Object.keys(qualification).length > 0 ? 'text-indigo-400' : 'text-[var(--text-muted)]'} />
                      {qualification && Object.keys(qualification).length > 0 ? 'View HubSpot qualification' : 'View HubSpot qualification (no data)'}
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        if (latestMsa?.signedUrl) {
                          window.open(latestMsa.signedUrl, '_blank', 'noopener,noreferrer')
                        } else {
                          showToast('No download URL available for this attachment', 'error')
                        }
                      }}
                      disabled={!latestMsa}
                      title={latestMsa?.name || ''}
                    >
                      <FileText size={14} className={latestMsa ? 'text-orange-400' : 'text-[var(--text-muted)]'} />
                      {latestMsa ? (
                        <span className="truncate">{latestMsa.name.length > 30 ? latestMsa.name.slice(0, 30) + '...' : latestMsa.name}</span>
                      ) : (
                        'Latest MSA (none found)'
                      )}
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        setDuplicateName(scope.customerName ? `${scope.customerName} (Copy)` : '')
                        setCloneHubSpotDeal(null)
                        clearSearch()
                        setShowDuplicateConfirm(true)
                      }}
                      disabled={!numericDealId || isDuplicating}
                    >
                      <Copy size={14} className="text-[var(--text-muted)]" />
                      Duplicate to new deal
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        setCloneFromDeal(null)
                        setCloneFromSearch('')
                        setCloneFromResults([])
                        setShowCloneFrom(true)
                      }}
                      disabled={!numericDealId || scope.finalized}
                    >
                      <Download size={14} className="text-[var(--text-muted)]" />
                      Clone from existing deal
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        window.open(`/dashboard/implementations/platform-journey?dealId=${numericDealId}`, '_blank', 'noopener,noreferrer')
                      }}
                      disabled={!numericDealId}
                      title="Opens the Platform Journey Prototype with AI Configure pre-loaded from this deal's Scope record"
                    >
                      <ExternalLink size={14} className="text-[var(--text-muted)]" />
                      Open Platform Journey Prototype
                    </button>

                    <div className="border-t border-[var(--border-color)] my-1" />
                    <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Blueprint</div>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                      onClick={() => {
                        setActionsOpen(false)
                        const text = generateBlueprintText(scope, qualification)
                        navigator.clipboard.writeText(text).then(() => showToast('Blueprint copied to clipboard', 'success')).catch(() => showToast('Failed to copy', 'error'))
                      }}
                    >
                      <Copy size={14} className="text-violet-400" />
                      Copy blueprint to clipboard
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                      onClick={() => {
                        setActionsOpen(false)
                        exportBlueprintPdf(scope, qualification)
                      }}
                    >
                      <FileText size={14} className="text-violet-400" />
                      Export blueprint to PDF
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        handleNextCallFocus()
                      }}
                      disabled={!numericDealId}
                    >
                      <Sparkles size={14} className="text-cyan-400" />
                      Next call focus
                    </button>

                    {isLocalDev && !hasLinkedDeal ? (
                      <button
                        type="button"
                        className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                        onClick={() => {
                          setActionsOpen(false)
                          void handleStartDemoSession()
                        }}
                      >
                        Start demo session
                      </button>
                    ) : null}

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        setShowTranscripts(true)
                      }}
                      disabled={!numericDealId}
                    >
                      <MessageSquareText size={14} className="text-blue-400" />
                      Call transcripts
                    </button>

                    <div className="border-t border-[var(--border-color)] my-1" />
                    <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Deal Status</div>

                    {!markedLost ? (
                      <button
                        type="button"
                        className="w-full px-4 py-2 text-left text-sm text-amber-400 hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                        onClick={async () => {
                          setActionsOpen(false)
                          if (!confirm('Mark this deal as lost? This will affect reporting in the Operational Overview.')) return
                          setMarkingLost(true)
                          const hubId = selectedDeal?.hubspotDealId || scope.hubspotDealId
                          const res = await presalesDashboardApi.markDealLost(presalesDealId || 0, true, hubId || undefined)
                          if (res.success) {
                            setMarkedLost(true)
                            showToast('Deal marked as lost', 'success')
                          } else {
                            showToast('Failed to mark deal as lost', 'error')
                          }
                          setMarkingLost(false)
                        }}
                        disabled={!hasLinkedDeal || markingLost || isContractSigned}
                      >
                        <Ban size={14} className="text-amber-400" />
                        {markingLost ? 'Updating...' : 'Mark as lost'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="w-full px-4 py-2 text-left text-sm text-green-400 hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                        onClick={async () => {
                          setActionsOpen(false)
                          setMarkingLost(true)
                          const hubId = selectedDeal?.hubspotDealId || scope.hubspotDealId
                          const res = await presalesDashboardApi.markDealLost(presalesDealId || 0, false, hubId || undefined)
                          if (res.success) {
                            setMarkedLost(false)
                            showToast('Deal unmarked as lost', 'success')
                          } else {
                            showToast('Failed to unmark deal', 'error')
                          }
                          setMarkingLost(false)
                        }}
                        disabled={!hasLinkedDeal || markingLost}
                      >
                        <CheckCircle size={14} className="text-green-400" />
                        {markingLost ? 'Updating...' : 'Unmark as lost'}
                      </button>
                    )}

                    <div className="border-t border-[var(--border-color)] my-1" />
                    <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Danger Zone</div>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        setShowClearConfirm(true)
                      }}
                      disabled={!numericDealId || scope.finalized || isClearing}
                    >
                      <Trash2 size={14} className="text-red-400" />
                      Clear all details
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        setShowDeleteConfirm(true)
                      }}
                      disabled={!numericDealId || scope.finalized || isDeleting || !!dealMeta?.hubspot_deal_id}
                      title={dealMeta?.hubspot_deal_id ? 'Cannot delete a deal linked to HubSpot' : undefined}
                    >
                      <Trash2 size={14} className="text-red-400" />
                      Delete scope & deal
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        setShowScopeLog(true)
                      }}
                      disabled={!numericDealId}
                    >
                      <History size={14} className="text-[var(--text-muted)]" />
                      Change log
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={async () => {
                        if (!numericDealId) return
                        setActionsOpen(false)
                        setSavingVersion(true)
                        const label = window.prompt('Version label (optional):')
                        if (label === null) { setSavingVersion(false); return }
                        try {
                          const res = await scopeVersionsApi.create(numericDealId, label || undefined)
                          if (res.success) showToast('Version saved', 'success')
                          else showToast(res.error || 'Failed to save version', 'error')
                        } catch { showToast('Failed to save version', 'error') }
                        finally { setSavingVersion(false) }
                      }}
                      disabled={!numericDealId || savingVersion}
                    >
                      <Save size={14} className="text-[var(--text-muted)]" />
                      {savingVersion ? 'Saving...' : 'Save version'}
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        setShowVersionHistory(true)
                      }}
                      disabled={!numericDealId}
                    >
                      <GitBranch size={14} className="text-[var(--text-muted)]" />
                      Version history
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={async () => {
                        if (!numericDealId) return
                        setActionsOpen(false)
                        setAddingToPP(true)
                        try {
                          // Calculate next Monday (or this Monday if today is Mon-Thu)
                          const now = new Date()
                          const day = now.getUTCDay()
                          const diff = day === 0 ? 1 : day <= 4 ? 1 - day : 8 - day
                          const monday = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + diff))
                          const weekStart = monday.toISOString().slice(0, 10)

                          // Get or auto-create the meeting for that week
                          const meetingRes = await pipelineProductsApi.getMeetingByWeek(weekStart)
                          if (!meetingRes.success || !meetingRes.data) {
                            showToast(meetingRes.error || 'Failed to find meeting', 'error')
                            return
                          }

                          const addRes = await pipelineProductsApi.addAgendaItem(meetingRes.data.id, {
                            item_type: 'scope',
                            deal_id: numericDealId,
                          })
                          if (addRes.success) {
                            showToast(`Added to Pipelines & Products (week of ${weekStart})`, 'success')
                          } else {
                            showToast(addRes.error || 'Failed to add to agenda', 'error')
                          }
                        } catch {
                          showToast('Failed to add to Pipelines & Products', 'error')
                        } finally {
                          setAddingToPP(false)
                        }
                      }}
                      disabled={!numericDealId || addingToPP}
                    >
                      {addingToPP ? <Loader2 size={14} className="animate-spin text-[var(--text-muted)]" /> : <CalendarPlus size={14} className="text-[var(--text-muted)]" />}
                      {addingToPP ? 'Adding...' : 'Add to Pipelines & Products'}
                    </button>

                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                      onClick={() => {
                        setActionsOpen(false)
                        window.open('/scope/schema', '_blank', 'noopener,noreferrer')
                      }}
                    >
                      <ClipboardList size={14} className="text-[var(--text-muted)]" />
                      View schema
                    </button>

                    <div className="border-t border-[var(--border-color)] my-1" />

                    <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Experimental</div>
                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2 disabled:opacity-50"
                      onClick={() => {
                        setActionsOpen(false)
                        handleExtractContractDetails()
                      }}
                      disabled={!selectedDeal?.hubspotDealId || scope.finalized || isExtractingContract}
                    >
                      {isExtractingContract ? <Loader2 size={14} className="animate-spin text-[var(--text-muted)]" /> : <Sparkles size={14} className="text-[var(--text-muted)]" />}
                      {isExtractingContract ? 'Extracting from contract...' : 'Extract name & ABN from contract'}
                    </button>

                    <div className="border-t border-[var(--border-color)] my-1" />

                    <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Export</div>
                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                      onClick={() => {
                        handleExportPdf()
                        setActionsOpen(false)
                      }}
                    >
                      <FileText size={14} className="text-primary" />
                      Export Scope to PDF
                    </button>
                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                      onClick={() => {
                        handleExportSow()
                        setActionsOpen(false)
                      }}
                    >
                      <FileText size={14} className="text-indigo-500" />
                      Export SOW
                    </button>

                    <div className="border-t border-[var(--border-color)] my-1" />

                    <div className="px-3 py-1.5 text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Workflow</div>
                    {scope.finalized ? (
                      <button
                        type="button"
                        className="w-full px-4 py-2 text-left text-sm text-amber-400 hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                        onClick={() => {
                          unfinalizeScope()
                          setActionsOpen(false)
                          showToast('Scope unlocked for editing', 'success')
                        }}
                      >
                        <Unlock size={14} />
                        Unlock scope
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="w-full px-4 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] flex items-center gap-2"
                        onClick={() => {
                          finalizeScope(user?.name || user?.email || 'Unknown')
                          setActionsOpen(false)
                          showToast('Scope locked', 'success')
                        }}
                      >
                        <Lock size={14} className="text-[var(--text-muted)]" />
                        Lock scope
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex w-full overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--bg-tertiary)]">
            {STATUS_STEPS.map((s, i) => {
              const activeIndex = STATUS_STEPS.findIndex((x) => x.id === scopeStatus)
              const isActive = s.id === scopeStatus
              const isDone = activeIndex > i

              const stageDate = formatStageDate(scope.generalInfo.scopeStatusDates?.[s.id])

              const isFirst = i === 0
              const isLast = i === STATUS_STEPS.length - 1
              const notch = 12

              const clipPath = isFirst
                ? `polygon(0 0, calc(100% - ${notch}px) 0, 100% 50%, calc(100% - ${notch}px) 100%, 0 100%)`
                : isLast
                  ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${notch}px 50%)`
                  : `polygon(0 0, calc(100% - ${notch}px) 0, 100% 50%, calc(100% - ${notch}px) 100%, 0 100%, ${notch}px 50%)`

              return (
                <button
                  key={s.id}
                  type="button"
                  className={`relative flex-1 px-3 py-2 text-xs font-medium transition-colors focus:outline-none disabled:opacity-50 border-r border-[var(--border-color)] ${
                    isActive
                      ? 'bg-primary text-white'
                      : isDone
                        ? 'bg-primary/10 text-primary'
                        : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
                  }`}
                  style={{
                    clipPath,
                    marginLeft: isFirst ? 0 : -notch,
                    paddingLeft: isFirst ? undefined : notch + 8,
                    zIndex: isActive ? 3 : isDone ? 2 : 1,
                  }}
                  onClick={() => setScopeStage(s.id)}
                  disabled={isLoading}
                >
                  <div className="leading-tight">
                    <div>{s.label}</div>
                    {s.id === 'implementations' && scope.sowConfig?.implementationsManager && (
                      <div className={`text-[10px] truncate ${isActive ? 'text-white/70' : isDone ? 'text-primary/60' : 'text-[var(--text-muted)]'}`}>
                        {scope.sowConfig.implementationsManager}
                      </div>
                    )}
                    <div className={isActive ? 'text-white/80' : isDone ? 'text-primary/70' : 'text-[var(--text-muted)]'}>
                      {stageDate || '—'}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Impl-review (PR 4): impl-team inject affordance, shown while the deal awaits review.
          Allowlist users get the push action; others get a read-only info panel (§3.6). */}
      {numericDealId && dealMeta?.impl_review_status === 'awaiting' && (
        <InjectImplFeeWidget
          dealId={numericDealId}
          scopeCalculatedFee={dealMeta.scope?.total_cost ?? 0}
          quoteCurrentFee={dealMeta.quote?.implementation_fee ?? 0}
          customerName={dealMeta.customer_name}
          submitterEmail={dealMeta.impl_review_requested_by}
          submitterName={dealMeta.impl_review_requested_by_name}
          onInjected={loadDealMeta}
        />
      )}

      <Modal
        isOpen={showClearConfirm}
        onClose={() => {
          if (isClearing) return
          setShowClearConfirm(false)
        }}
        title="Clear all details"
        size="md"
      >
        <div className="space-y-3 text-sm text-[var(--text-secondary)]">
          <p className="text-sm text-[var(--text-primary)] font-medium">Are you sure?</p>
          <p>This will delete all detail without retrieval.</p>
        </div>

        <ModalFooter>
          <button
            type="button"
            className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50"
            onClick={() => setShowClearConfirm(false)}
            disabled={isClearing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            onClick={async () => {
              await handleClearAllDetails()
              setShowClearConfirm(false)
            }}
            disabled={!dealId || isClearing}
          >
            {isClearing ? 'Clearing...' : 'Yes, clear all details'}
          </button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          if (isDeleting) return
          setShowDeleteConfirm(false)
        }}
        title="Delete scope & deal"
        size="md"
      >
        <div className="space-y-3 text-sm text-[var(--text-secondary)]">
          <p className="text-sm text-[var(--text-primary)] font-medium">Are you sure?</p>
          <p>This will permanently delete this placeholder deal and its scope, quote, and all related data. This cannot be undone.</p>
        </div>

        <ModalFooter>
          <button
            type="button"
            className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            onClick={async () => {
              if (!numericDealId) return
              setIsDeleting(true)
              const res = await dealsApi.delete(numericDealId)
              if (res.success) {
                showToast('Deal and scope deleted', 'success')
                setShowDeleteConfirm(false)
                navigate('/')
              } else {
                showToast(res.error || 'Failed to delete deal', 'error')
                setIsDeleting(false)
              }
            }}
            disabled={!numericDealId || isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Yes, delete permanently'}
          </button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showLinkConfirm}
        onClose={() => {
          if (isLinking) return
          setShowLinkConfirm(false)
          setLinkSelectedDeal(null)
        }}
        title="Link to HubSpot deal"
        size="md"
      >
        <div className="space-y-3 text-sm text-[var(--text-secondary)]">
          <p className="text-sm text-[var(--text-primary)] font-medium">Are you sure?</p>
          <p>This will permanently link this placeholder scope to the HubSpot deal:</p>
          <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <div className="text-sm font-medium text-[var(--text-primary)]">{linkSelectedDeal?.name}</div>
            <div className="text-xs text-[var(--text-muted)]">HubSpot ID: #{linkSelectedDeal?.id}</div>
          </div>
          <p className="text-xs text-amber-400">This action cannot be undone. The scope data will be preserved.</p>
        </div>

        <ModalFooter>
          <button
            type="button"
            className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50"
            onClick={() => { setShowLinkConfirm(false); setLinkSelectedDeal(null) }}
            disabled={isLinking}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            onClick={handleConfirmLinkHubspot}
            disabled={isLinking}
          >
            {isLinking ? <><Loader2 size={14} className="animate-spin" /> Linking...</> : 'Yes, link this deal'}
          </button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showDuplicateConfirm}
        onClose={() => {
          if (isDuplicating) return
          setShowDuplicateConfirm(false)
        }}
        title="Duplicate to new deal"
        size="md"
      >
        <div className="space-y-3 text-sm text-[var(--text-secondary)]">
          <p>This will create a new deal, link it to a HubSpot deal, and copy all scope settings from the current deal.</p>
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">New deal customer name</label>
            <input
              type="text"
              className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              placeholder="(Optional)"
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              disabled={isDuplicating}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              Link duplicated deal to HubSpot deal
            </label>

            {cloneHubSpotDeal ? (
              <div className="flex items-center justify-between h-12 bg-primary/10 border border-primary/30 rounded-lg px-4">
                <div className="text-sm text-[var(--text-primary)] truncate">{cloneHubSpotDeal.name}</div>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline disabled:opacity-50"
                  onClick={() => setCloneHubSpotDeal(null)}
                  disabled={isDuplicating}
                >
                  Change
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                  placeholder="Search HubSpot deals (min 2 chars)"
                  value={searchQuery}
                  onChange={(e) => {
                    const v = e.target.value
                    setSearchQuery(v)
                    if (v.length >= 2) {
                      void searchDeals(v)
                    }
                  }}
                  disabled={isDuplicating}
                />

                {(searchQuery.length >= 2 || hasSearched) && (
                  <div className="mt-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg max-h-64 overflow-y-auto">
                    {isSearching ? (
                      <div className="p-3 text-xs text-[var(--text-muted)]">Searching HubSpot...</div>
                    ) : searchResults.length > 0 ? (
                      <div className="divide-y divide-[var(--border-color)]">
                        {searchResults.map((d) => (
                          <button
                            key={d.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-[var(--bg-secondary)] transition-colors"
                            onClick={() => {
                              setCloneHubSpotDeal({ id: d.id, name: d.name })
                              clearSearch()
                            }}
                            disabled={isDuplicating}
                          >
                            <div className="text-sm text-[var(--text-primary)] truncate">{d.name}</div>
                            <div className="text-xs text-[var(--text-muted)] truncate">#{d.id}</div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 text-xs text-[var(--text-muted)]">No deals found.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="text-[var(--text-primary)] font-medium">Continue?</p>
        </div>
        <ModalFooter>
          <button
            type="button"
            className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50"
            onClick={() => setShowDuplicateConfirm(false)}
            disabled={isDuplicating}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            onClick={handleDuplicateDeal}
            disabled={isDuplicating || !cloneHubSpotDeal}
          >
            {isDuplicating ? 'Duplicating...' : 'Duplicate'}
          </button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showCloneFrom}
        onClose={() => {
          if (isCloning) return
          setShowCloneFrom(false)
        }}
        title="Clone scope from existing deal"
        size="md"
      >
        <div className="space-y-3 text-sm text-[var(--text-secondary)]">
          <p>Select an existing deal to copy its scope settings into this deal. This will <strong className="text-[var(--text-primary)]">overwrite</strong> the current scope (customer name and status are preserved).</p>

          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              Search deals
            </label>

            {cloneFromDeal ? (
              <div className="flex items-center justify-between h-12 bg-primary/10 border border-primary/30 rounded-lg px-4">
                <div className="text-sm text-[var(--text-primary)] truncate">{cloneFromDeal.name}</div>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline disabled:opacity-50"
                  onClick={() => setCloneFromDeal(null)}
                  disabled={isCloning}
                >
                  Change
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                  placeholder="Search by customer name or deal name (min 2 chars)"
                  value={cloneFromSearch}
                  onChange={(e) => handleCloneFromSearch(e.target.value)}
                  disabled={isCloning}
                />

                {(cloneFromSearch.length >= 2) && (
                  <div className="mt-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg max-h-64 overflow-y-auto">
                    {cloneFromSearching ? (
                      <div className="p-3 text-xs text-[var(--text-muted)]">Searching...</div>
                    ) : cloneFromResults.length > 0 ? (
                      <div className="divide-y divide-[var(--border-color)]">
                        {cloneFromResults.map((d) => (
                          <button
                            key={d.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-[var(--bg-secondary)] transition-colors"
                            onClick={() => {
                              setCloneFromDeal({ id: d.id, name: d.customer_name || d.hubspot_deal_name || `Deal #${d.id}` })
                              setCloneFromSearch('')
                              setCloneFromResults([])
                            }}
                            disabled={isCloning}
                          >
                            <div className="text-sm text-[var(--text-primary)] truncate">
                              {d.customer_name || <span className="text-[var(--text-muted)]">Unnamed</span>}
                            </div>
                            {d.hubspot_deal_name && (
                              <div className="text-xs text-[var(--text-muted)] truncate">{d.hubspot_deal_name}</div>
                            )}
                            <div className="text-[10px] text-[var(--text-muted)]">#{d.id}</div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 text-xs text-[var(--text-muted)]">No deals found.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {cloneFromDeal && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <AlertCircle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-400/80">
                This will replace all scope config (KYC, AML, KYB, rules, hours, etc.) with the settings from the selected deal.
              </p>
            </div>
          )}
        </div>
        <ModalFooter>
          <button
            type="button"
            className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors disabled:opacity-50"
            onClick={() => setShowCloneFrom(false)}
            disabled={isCloning}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            onClick={handleCloneFromDeal}
            disabled={isCloning || !cloneFromDeal}
          >
            {isCloning ? 'Cloning...' : 'Clone scope'}
          </button>
        </ModalFooter>
      </Modal>

      {/* Locked Banner */}
      {scope.finalized && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="text-amber-400" size={20} />
            <div>
              <p className="text-sm font-medium text-amber-400">This scope is locked</p>
              <p className="text-xs text-amber-400/80">
                {scopeStatus === 'handover' || scopeStatus === 'sent' ? 'Automatically locked. ' : ''}
                Locked by {scope.finalizedBy} on{' '}
                {scope.finalizedAt ? new Date(scope.finalizedAt).toLocaleDateString() : 'Unknown date'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 transition-colors"
            onClick={() => {
              unfinalizeScope()
              showToast('Scope unlocked for editing', 'success')
            }}
          >
            <Unlock size={12} />
            Unlock
          </button>
        </div>
      )}

      {/* Customer Name */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Customer Name</label>
            <input
              type="text"
              className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              placeholder="Enter customer name"
              value={scope.customerName}
              onChange={(e) => updateCustomerName(e.target.value)}
              disabled={scope.finalized || !hasLinkedDeal}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">ABN</label>
            <input
              type="text"
              className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              placeholder="Optional — e.g., 12 345 678 901"
              value={scope.customerAbn}
              onChange={(e) => updateCustomerAbn(e.target.value)}
              disabled={scope.finalized || !hasLinkedDeal}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Progress</label>
            <div className="h-12 flex flex-col justify-center gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-[var(--text-muted)] w-16 shrink-0">Overall</span>
                <div className="flex-1 h-2.5 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      overallCompletion === 100 ? 'bg-success' : overallCompletion > 50 ? 'bg-warning' : 'bg-primary'
                    }`}
                    style={{ width: `${overallCompletion}%` }}
                  />
                </div>
                <span className={`text-xs font-bold w-8 text-right ${getCompletionColor(overallCompletion)}`}>{overallCompletion}%</span>
              </div>
              {(() => {
                const bpTab = tabCompletions.blueprint ?? 0
                const meddpiccCount = qualification ? ['vision___success_criteria', 'economic_buyer', 'decision_criteria', 'decision_process', 'paper_process', 'identify_pain', 'champion', 'competitor'].filter((k) => qualification[k]?.trim()).length : 0
                const meddpiccPct = Math.round((meddpiccCount / 8) * 100)
                const bpCompletion = Math.round(bpTab * 0.8 + meddpiccPct * 0.2)
                return (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-[var(--text-muted)] w-16 shrink-0">Blueprint</span>
                    <div className="flex-1 h-2.5 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          bpCompletion === 100 ? 'bg-success' : bpCompletion > 50 ? 'bg-warning' : 'bg-primary'
                        }`}
                        style={{ width: `${bpCompletion}%` }}
                      />
                    </div>
                    <span className={`text-xs font-bold w-8 text-right ${getCompletionColor(bpCompletion)}`}>{bpCompletion}%</span>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>

        {/* Deal Link Status */}
        <div className="mt-4 border border-[var(--border-color)] rounded-lg bg-[var(--bg-tertiary)] px-3 py-2">
          {selectedDeal ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <Link
                  to="/home"
                  className="flex items-center gap-2 min-w-0 flex-1 hover:opacity-90 transition-opacity"
                >
                  <div className="shrink-0 w-7 h-7 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center">
                    <Building2 size={14} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[var(--text-primary)] truncate">{selectedDeal.name}</div>
                    <div className="text-xs text-[var(--text-muted)] truncate">
                      {selectedDeal.hubspotDealId ? `Linked deal • #${selectedDeal.hubspotDealId}` : `Placeholder deal • #${selectedDeal.id}`}
                    </div>
                  </div>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  {!selectedDeal.hubspotDealId && (
                    <button
                      onClick={() => setShowLinkHubspot(!showLinkHubspot)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-md hover:bg-amber-500/20 transition-colors"
                    >
                      <LinkIcon size={12} />
                      Link to HubSpot
                    </button>
                  )}
                  <Link to="/home" className="text-xs text-primary hover:underline">Change</Link>
                </div>
              </div>
              {showLinkHubspot && !selectedDeal.hubspotDealId && (
                <div className="mt-2 pt-2 border-t border-[var(--border-color)]">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      {linkSearching ? <Loader2 size={14} className="text-[var(--text-muted)] animate-spin" /> : <Search size={14} className="text-[var(--text-muted)]" />}
                    </div>
                    <input
                      type="text"
                      className="w-full h-9 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-md pl-8 pr-3 text-sm text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-amber-500"
                      placeholder="Search HubSpot deals..."
                      value={linkSearch}
                      onChange={(e) => handleLinkHubspotSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  {linkSearchResults.length > 0 && (
                    <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)]">
                      {linkSearchResults.map((deal) => (
                        <button
                          key={deal.id}
                          className="w-full px-3 py-2 text-left hover:bg-[var(--bg-tertiary)] transition-colors border-b border-[var(--border-color)] last:border-b-0"
                          onClick={() => {
                            setLinkSelectedDeal({ id: deal.id, name: deal.name })
                            setShowLinkConfirm(true)
                          }}
                        >
                          <div className="text-sm font-medium text-[var(--text-primary)] truncate">{deal.name}</div>
                          <div className="text-xs text-[var(--text-muted)]">
                            {deal.company && `${deal.company} • `}#{deal.id}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : hasLinkedDeal ? (
            <Link
              to="/home"
              className="flex items-center justify-between gap-3 hover:opacity-90 transition-opacity"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="shrink-0 w-7 h-7 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <Building2 size={14} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[var(--text-primary)] truncate">DealHub session</div>
                  <div className="text-xs text-[var(--text-muted)] truncate">Session • #{dealId}</div>
                </div>
              </div>
              <div className="text-xs text-primary">View</div>
            </Link>
          ) : (
            <Link
              to="/home"
              className="flex items-center justify-between gap-3 hover:opacity-90 transition-opacity"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="shrink-0 w-7 h-7 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                  <Building2 size={14} className="text-amber-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[var(--text-primary)] truncate">No deal linked</div>
                  <div className="text-xs text-[var(--text-muted)] truncate">Link a deal to enable auto-save and exports</div>
                </div>
              </div>
              <div className="text-xs text-amber-400">Link</div>
            </Link>
          )}
        </div>

        {/* Reseller parent indicator */}
        {resellerConstraints && parentCustomerName && (
          <div className="mt-3 border border-blue-500/30 rounded-lg bg-blue-500/10 px-3 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="shrink-0 w-7 h-7 rounded-md bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                  <GitBranch size={14} className="text-blue-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-blue-300 truncate">Reseller child of {parentCustomerName}</div>
                  <div className="text-xs text-blue-400/70 truncate">
                    KYC, AML, IDV, KYB, Recipes, Fraud & Technical Setup inherited and locked from parent
                  </div>
                </div>
              </div>
              <div className="text-xs text-blue-400 shrink-0">Manage in Advanced tab</div>
            </div>
          </div>
        )}

        {/* Reseller children list — shown when this scope is a reseller parent */}
        {resellerChildren.length > 0 && (
          <div className="mt-3 border border-purple-500/30 rounded-lg bg-purple-500/10 px-3 py-2.5">
            <div className="flex items-center gap-2 mb-2">
              <GitBranch size={14} className="text-purple-400" />
              <span className="text-sm font-medium text-purple-300">
                Reseller Children ({resellerChildren.length})
              </span>
            </div>
            <div className="space-y-1">
              {resellerChildren.map((child) => (
                <a
                  key={child.deal_id}
                  href={`/scope?session=${child.deal_id}`}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-purple-500/10 transition-colors group"
                >
                  <div className="min-w-0">
                    <span className="text-sm text-[var(--text-primary)] truncate block">
                      {child.customer_name || child.hubspot_deal_name || `Deal #${child.deal_id}`}
                    </span>
                  </div>
                  <span className="text-xs text-purple-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    Open scope →
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Upsell banner — visible across all tabs */}
        {isUpsell && (
          <div className="mt-3 border border-primary/30 rounded-lg bg-primary/5 px-3 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <ArrowUpCircle size={15} className="text-primary shrink-0" />
                <div className="min-w-0">
                  <span className="text-sm font-medium text-primary">Upsell — Changes Only</span>
                  <span className="text-xs text-[var(--text-secondary)] ml-2">
                    This scope should only reflect what is changing, not the existing setup.
                  </span>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={scope.generalInfo.scopeConfirmed || false}
                  onChange={(e) => updateGeneralInfo('scopeConfirmed', e.target.checked)}
                  disabled={scope.finalized}
                  className="rounded border-[var(--border-color)] text-success focus:ring-success h-4 w-4"
                />
                <span className="text-xs font-medium text-[var(--text-primary)]">Scope confirmed</span>
                {scope.generalInfo.scopeConfirmed && <CheckCircle2 size={13} className="text-success" />}
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Configuration Progress */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">Configuration Progress</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(tabCompletions).filter(([tab]) => !EXCLUDED_FROM_OVERALL.has(tab)).map(([tab, completion]) => (
            <div key={tab} className="flex items-center gap-2">
              <div className="flex-1">
                <div className="text-xs text-[var(--text-secondary)]">{getProgressLabel(tab)}</div>
                <div className="h-1.5 bg-[var(--bg-tertiary)] rounded-full mt-1">
                  <div
                    className={`h-full rounded-full transition-all ${
                      completion === 100 ? 'bg-success' : completion > 50 ? 'bg-warning' : 'bg-primary'
                    }`}
                    style={{ width: `${completion}%` }}
                  />
                </div>
              </div>
              <span className="text-xs text-[var(--text-muted)]">{completion}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
        <div className="border-b border-[var(--border-color)] overflow-x-auto">
          <div className="flex">
            {TABS.map((tab) => {
              const completion = tabCompletions[tab.completionKey] ?? 0
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-gray-600'
                  } ${!hasLinkedDeal ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => {
                    if (!hasLinkedDeal) {
                      showToast('No deal linked. Go to Home and select a deal before editing.', 'warning', 2500)
                      return
                    }
                    setActiveTab(tab.id)
                  }}
                  disabled={!hasLinkedDeal}
                >
                  {tab.label}
                  {EXCLUDED_FROM_OVERALL.has(tab.completionKey) ? (
                    completion > 0 ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-success" />
                    ) : null
                  ) : (
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        completion === 100
                          ? 'bg-success/20 text-success'
                          : completion > 0
                            ? 'bg-warning/20 text-warning'
                            : 'bg-gray-700 text-[var(--text-secondary)]'
                      }`}
                    >
                      {completion}%
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6 relative">
          {!hasLinkedDeal ? (
            <EmptyState
              icon={ClipboardList}
              title="No scope started"
              description="Select a deal from the home page to start scoping"
              action={{ label: "Go to Home", onClick: () => navigate("/home") }}
            />
          ) : (
            renderTabContent()
          )}
        </div>
      </div>

      {/* Validation Warning */}
      {overallCompletion < 100 && !scope.finalized && activeTab !== 'blueprint' && (
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-warning" size={20} />
          <div>
            <p className="text-sm font-medium text-warning">Incomplete Configuration</p>
            <p className="text-xs text-warning/80">
              Please complete all required fields before finalizing. Current progress: {overallCompletion}%
            </p>
          </div>
        </div>
      )}

      {showScopeLog && numericDealId && (
        <ScopeLogModal dealId={numericDealId} onClose={() => setShowScopeLog(false)} />
      )}

      {numericDealId && (
        <TranscriptsModal dealId={numericDealId} isOpen={showTranscripts} onClose={() => setShowTranscripts(false)} />
      )}

      {showNextCallFocus && (
        <Modal title="Next Call Focus" isOpen={showNextCallFocus} onClose={() => setShowNextCallFocus(false)}>
          {nextCallLoading && (
            <div className="flex items-center gap-3 py-8 justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
              <span className="text-sm text-[var(--text-secondary)]">Analysing blueprint...</span>
            </div>
          )}
          {nextCallError && (
            <p className="text-sm text-red-400 py-4">{nextCallError}</p>
          )}
          {nextCallItems && (
            <div className="space-y-3">
              <p className="text-xs text-[var(--text-muted)]">Priority items to capture or validate on the next customer call.</p>
              <ul className="space-y-2">
                {nextCallItems.map((text, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-400 text-[10px] font-bold flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm text-[var(--text-primary)]">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ModalFooter>
            <div className="flex items-center gap-2">
              {nextCallItems && (
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/25 rounded-lg hover:bg-cyan-500/20 transition-colors flex items-center gap-2"
                  onClick={() => {
                    const text = nextCallItems.map((t, i) => `${i + 1}. ${t}`).join('\n')
                    navigator.clipboard.writeText(text)
                      .then(() => showToast('Copied to clipboard', 'success'))
                      .catch(() => showToast('Failed to copy', 'error'))
                  }}
                >
                  <Copy size={14} /> Copy to clipboard
                </button>
              )}
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-[var(--text-secondary)] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] transition-colors"
                onClick={() => setShowNextCallFocus(false)}
              >
                Close
              </button>
            </div>
          </ModalFooter>
        </Modal>
      )}

      {showQuotePreview && linkedQuote && (
        <QuotePreviewModal quote={linkedQuote} onClose={() => setShowQuotePreview(false)} />
      )}

      {showQualification && qualification && Object.keys(qualification).length > 0 && (
        <QualificationModal qualification={qualification} onClose={() => setShowQualification(false)} />
      )}


      {showVersionHistory && numericDealId && (
        <VersionHistoryModal
          dealId={numericDealId}
          onClose={() => setShowVersionHistory(false)}
          onRestore={() => {
            setShowVersionHistory(false)
            showToast('Version restored. Reloading scope...', 'success')
            window.location.reload()
          }}
        />
      )}

      </div>
    </div>
  )
}
